import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { faCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Button, InputField, Textarea } from '@rocketlane/ui';
import { CustomSectionState } from 'api/custom-section/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { CustomGroupSectionComponentProps } from '../../types';

const RemoveButton = styled(Button)`
  &:hover {
    color: var(--red-60) !important;
  }
`;

const Footer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
  justify-content: flex-end;
`;
const Wrapper = styled.div`
  padding: var(--spacing-05);
  border-bottom-left-radius: var(--border-radius-small);
  border-bottom-right-radius: var(--border-radius-small);
  border: 1px solid var(--field-border-default);
  border-top: none;
  outline: 2px solid var(--blue-80);
  outline-offset: -2px;
`;

type FormValues = {
  title: string;
  description: string;
};

interface Props extends CustomGroupSectionComponentProps {
  onEditDone: () => void;
}
export const CustomGroupSectionPromptInput: FC<Props> = ({
  node,
  onEditDone,
  onDone,
  onRemove,
}) => {
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.custom_group_section.fields.title.placeholder',
  );
  const descriptionPlaceholder = useTranslation(
    'rich_text_editor.sections.custom_group_section.fields.description.placeholder',
  );
  const [form] = Form.useForm<FormValues>();
  const [hasError, setHasError] = useState(false);
  const onValuesChange = useCallback(() => {
    queueMacrotask(() => {
      form
        .validateFields()
        .then(() => {
          setHasError(false);
        })
        .catch(() => {
          setHasError(true);
        });
    });
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({
      title: node.attrs.title,
      description: node.attrs.config.description,
    });
    onValuesChange();
  }, [form, node.attrs.title, node.attrs.config.description, onValuesChange]);
  const [actionLoading, setActionLoading] = useState(false);
  const onSubmit = useCallback(() => {
    form.validateFields().then((values) => {
      setActionLoading(true);
      onDone({
        ...node.attrs,
        state: CustomSectionState.Closed,
        title: values.title,
        config: {
          ...node.attrs.config,
          description: values.description,
        },
      }).then(() => {
        setActionLoading(false);
        onEditDone();
      });
    });
  }, [form, node.attrs, onDone, onEditDone]);

  return (
    <Wrapper>
      <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
        <Form.Item
          name="title"
          label={
            <FormattedMessage id="rich_text_editor.sections.custom_group_section.fields.title.label" />
          }
          rules={[{ required: true, message: '' }]}
          validateStatus={'success'}
          requiredMark={'optional'}
        >
          <InputField placeholder={titlePlaceholder} />
        </Form.Item>
        <Form.Item
          name="description"
          label={
            <FormattedMessage id="rich_text_editor.sections.custom_group_section.fields.description.label" />
          }
          validateStatus={'success'}
          hidden={true}
        >
          <Textarea placeholder={descriptionPlaceholder} />
        </Form.Item>
      </Form>
      <Footer>
        <RemoveButton
          leftIcon={<FontAwesomeIcon icon={faTrash} size={14} />}
          onClick={onRemove}
          variant="ghost"
          disabled={actionLoading}
        />
        <Button
          leftIcon={<FontAwesomeIcon icon={faCheck} size={14} />}
          onClick={onSubmit}
          variant="primary"
          disabled={hasError}
          loading={actionLoading}
        >
          <FormattedMessage id="done" />
        </Button>
      </Footer>
    </Wrapper>
  );
};
